import React from 'react';
import { MapPin, Sparkles, Clock, Users, Wallet, X, ArrowDown } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import TripStyleBadge from '../TripStyleBadge';

/**
 * TripSummaryCard — pinned premium "concierge folder" card that surfaces
 * the active trip plan above the chat scroll. Clicking the close button
 * clears the entire chat (single source of truth — replicates the AI
 * starter state).
 *
 * Props:
 *   tripPlan     {destination, dates, travelers, totalBudget|totalEstimate, tripStyle}
 *   onClear      callback to wipe state + localStorage + reset welcome
 */
export default function TripSummaryCard({ tripPlan, onClear, onJumpToBookings }) {
  const { t } = useLanguage();
  const destinations = Array.isArray(tripPlan?.destinations) ? tripPlan.destinations.map(d => typeof d === 'string' ? d : (d?.city || d?.name || d?.destination || '')).filter(Boolean) : [];
  if (!tripPlan?.destination && !destinations.length) return null;
  const destinationLabel = destinations.length > 1 ? destinations.join(' → ') : (tripPlan.destination || destinations[0]);
  const total = tripPlan.totalBudget || tripPlan.totalEstimate;
  return (
    <div
      className="px-4 py-3 bg-gradient-to-r from-gray-900 via-gray-900 to-blue-950 border-b border-gray-800"
      data-testid="chat-trip-summary"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 bg-white/10 rounded-xl flex items-center justify-center flex-shrink-0 ring-1 ring-white/10">
            <MapPin className="w-5 h-5 text-cyan-300" />
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-cyan-300/80 font-bold">
              <Sparkles className="w-3 h-3" />
              {t('chat.summaryTitle')}
              <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md bg-brand-amber-400 text-brand-amber-900 text-[9px] font-extrabold tracking-wider" data-testid="chip-ai-recommendation">
                <Sparkles className="w-2.5 h-2.5" />
                {t('chip.aiRecommendation') || 'AI RECOMMENDATION'}
              </span>
              {tripPlan.tripStyle && <TripStyleBadge style={tripPlan.tripStyle} />}
            </div>
            <div className="font-bold text-white whitespace-normal break-words text-base sm:text-lg leading-tight mt-0.5">{destinationLabel}</div>
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] sm:text-xs text-blue-100/70 mt-1">
              {tripPlan.dates && (
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {tripPlan.dates}
                </span>
              )}
              {tripPlan.travelers && (
                <span className="flex items-center gap-1">
                  <Users className="w-3 h-3" />
                  {tripPlan.travelers} {t('chat.summaryTravelers')}
                </span>
              )}
              {total && (
                <span
                  className="flex items-center gap-1 text-cyan-200 font-bold"
                  data-testid="chat-trip-total"
                >
                  <Wallet className="w-3 h-3" />
                  {total}
                </span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {onJumpToBookings && (
            <button
              type="button"
              onClick={onJumpToBookings}
              data-testid="chat-trip-jump-to-bookings"
              className="hidden sm:inline-flex items-center gap-1 text-[11px] font-semibold text-cyan-200 hover:text-white bg-white/10 hover:bg-white/20 px-2.5 py-1 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400"
              title={t('chat.jumpToBookings')}
            >
              {t('chat.jumpToBookings')}
              <ArrowDown className="w-3 h-3" />
            </button>
          )}
          <button
            onClick={onClear}
            data-testid="chat-trip-summary-clear"
            className="flex items-center gap-1 text-[11px] font-semibold text-blue-200/70 hover:text-white transition-colors px-2 py-1 rounded-md hover:bg-white/10"
            title={t('chat.summaryClear')}
          >
            <X className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">{t('chat.summaryClear')}</span>
          </button>
        </div>
      </div>
    </div>
  );
}
